import { useState, useEffect } from 'react'
import { Link2, Loader2 } from 'lucide-react'
import { cn } from '../../utils/cn'

import { DreamService } from '../../services/dreamService'
import { GoalService } from '../../services/goalService'
import { SkillService } from '../../services/skillService'
import { HabitService } from '../../services/habitService'
import { TaskService } from '../../services/taskService'
import { RoadblockService } from '../../services/roadblockService'

export type ParentType = 'dream' | 'goal' | 'skill' | 'habit' | 'task' | 'roadblock'

const TYPE_LABELS: Record<ParentType, string> = {
  dream: 'Dream',
  goal: 'Goal',
  skill: 'Skill',
  habit: 'Habit',
  task: 'Task',
  roadblock: 'Roadblock',
}

const PARENT_FIELD_MAP: Record<ParentType, string> = {
  dream: 'parent_dream_id',
  goal: 'parent_goal_id',
  skill: 'parent_skill_id',
  habit: 'parent_habit_id',
  task: 'parent_task_id',
  roadblock: 'parent_roadblock_id',
}

interface ParentPickerProps {
  allowedTypes: ParentType[]
  parentType: ParentType | ''
  parentId: number | string | null
  onChange: (fields: Record<string, any>) => void
  className?: string
}

export function ParentPicker({ allowedTypes, parentType, parentId, onChange, className }: ParentPickerProps) {
  const [items, setItems] = useState<{ id: number | string; title: string }[]>([])
  const [loading, setLoading] = useState(false)
  // Empty vs unreachable (defect found 2026-08-30 by tests/ui-behavior):
  // a failed parent fetch used to `setItems([])` silently, so the picker was
  // indistinguishable from a genuinely empty account. The user then attached
  // the new item to NOTHING, believing they had no goals to attach it to.
  const [loadFailed, setLoadFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!parentType) {
      setItems([])
      setLoadFailed(false)
      return
    }
    let cancelled = false
    setLoading(true)
    fetchItemsForType(parentType).then(fetched => {
      if (!cancelled) {
        setItems(fetched)
        setLoadFailed(false)
        setLoading(false)
      }
    }).catch(() => {
      if (!cancelled) {
        setItems([])
        setLoadFailed(true)
        setLoading(false)
      }
    })
    return () => { cancelled = true }
  }, [parentType, reloadKey])

  const handleTypeChange = (newType: string) => {
    const clearFields: Record<string, any> = { parent_type: newType || '', parent_id: null }
    for (const t of allowedTypes) {
      clearFields[PARENT_FIELD_MAP[t]] = undefined
    }
    onChange(clearFields)
  }

  const handleItemChange = (newId: string) => {
    const parsed = newId ? (isNaN(Number(newId)) ? newId : Number(newId)) : null
    const clearFields: Record<string, any> = { parent_type: parentType, parent_id: parsed }
    for (const t of allowedTypes) {
      clearFields[PARENT_FIELD_MAP[t]] = undefined
    }
    if (parentType && parsed != null) {
      clearFields[PARENT_FIELD_MAP[parentType]] = parsed
    }
    onChange(clearFields)
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleTypeChange('')}
          className={cn(
            'px-3 py-1.5 rounded-lg text-sm font-medium border transition-all',
            !parentType
              ? 'border-brand bg-brand-subtle text-brand'
              : 'border-soft text-secondary hover:border-strong'
          )}
        >
          None
        </button>
        {allowedTypes.map(t => (
          <button
            key={t}
            type="button"
            onClick={() => handleTypeChange(t)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium border transition-all',
              parentType === t
                ? 'border-brand bg-brand-subtle text-brand'
                : 'border-soft text-secondary hover:border-strong'
            )}
          >
            {TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {parentType && (
        <div className="relative">
          {loading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading {TYPE_LABELS[parentType]}s...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-muted flex-shrink-0" />
              <select
                value={parentId != null ? String(parentId) : ''}
                onChange={e => handleItemChange(e.target.value)}
                className="w-full px-4 py-2.5 border border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-brand transition-all"
              >
                <option value="">Select a {TYPE_LABELS[parentType]}...</option>
                {items.map(item => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {item.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!loading && loadFailed && (
            <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-bad">
              <span>
                We could not load your {TYPE_LABELS[parentType]}s just now, so this
                list is not showing them.
              </span>
              <button
                type="button"
                onClick={() => setReloadKey((k) => k + 1)}
                className="underline hover:no-underline font-medium"
              >
                Try again
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

async function fetchItemsForType(type: ParentType): Promise<{ id: number | string; title: string }[]> {
  switch (type) {
    case 'dream': {
      const svc = new DreamService()
      const items = await svc.getDreams()
      return items.map(d => ({ id: d.id, title: d.title }))
    }
    case 'goal': {
      const svc = new GoalService()
      const items = await svc.getGoals()
      return items.map(g => ({ id: g.id, title: g.title }))
    }
    case 'skill': {
      const svc = new SkillService()
      const items = await svc.getSkills()
      return items.map(s => ({ id: s.id, title: s.title }))
    }
    case 'habit': {
      const svc = new HabitService()
      const items = await svc.getHabits()
      return items.map(h => ({ id: h.id, title: h.title }))
    }
    case 'task': {
      const svc = new TaskService()
      const items = await svc.getTasks()
      return items.map(t => ({ id: t.id, title: t.title }))
    }
    case 'roadblock': {
      const svc = new RoadblockService()
      const items = await svc.getRoadblocks()
      return items.map(r => ({ id: r.id, title: r.title }))
    }
    default:
      return []
  }
}

export { PARENT_FIELD_MAP }
